import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Drawer,
  Space,
  Table,
  Tag,
  Select,
  Input,
  Typography,
  Tooltip,
  message,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  listSubmissions,
  promoteSubmissions,
  rejectSubmissions,
  restoreSubmission,
  retierSubmission,
} from "../../../redux/actions/formsAction";
import axios from "axios";
import { buildApiUrl } from "../../../utils/apiHelper";
import { useFormDetail } from "./FormDetailContext";
import { IDENTITY_FIELD_NAMES } from "./formUtils";
import useTableScrollY from "../../../hooks/useTableScrollY";

const { Text } = Typography;

const DEFAULT_PAGINATION = { current: 1, pageSize: 20 };

function formatCustomFieldValue(value) {
  if (value == null || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.join(", ") || "—";
  return String(value);
}

const statusColors = {
  received: "blue",
  pending_confirm: "orange",
  confirmed: "green",
  spam: "red",
  rejected: "default",
};

function spamScoreColor(record) {
  if (record.spamScore == null) return "default";
  if (record.antiSpamWouldEnforce) return "red";
  if (record.spamScore >= 26) return "orange";
  return "green";
}

function formatSpamReasons(reasons) {
  if (!reasons?.length) return "No signals";
  return reasons
    .map((r) => {
      const sign = r.weight >= 0 ? "+" : "";
      return `${r.reason} (${sign}${r.weight})`;
    })
    .join(", ");
}

// PRD item 4 (FR-4.5/FR-4.7), task 4.6.
const LEAD_TIER_COLORS = { hot: "red", warm: "orange", cold: "blue" };

function humanizeSignalName(name) {
  return String(name || "")
    .split("_")
    .join(" ");
}

export default function FormSubmissionsPanel() {
  const { id: formId, formRecord } = useFormDetail();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const submissionList = useSelector((state) => state.forms.submissions);
  const submissionCount = useSelector((state) => state.forms.submissionCount);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [filters, setFilters] = useState({});
  const [emailInput, setEmailInput] = useState("");
  const [detail, setDetail] = useState(null);
  const [tableParams, setTableParams] = useState({
    pagination: { ...DEFAULT_PAGINATION, total: 0 },
  });
  const [tableAreaRef, tableScrollY] = useTableScrollY([
    loading,
    submissionList.length,
    tableParams.pagination,
  ]);

  const fetchSubmissions = useCallback(
    (pagination, nextFilters = filters) => {
      const page = pagination || tableParams.pagination;
      setLoading(true);
      const payload = {
        pageSize: page.pageSize,
        pageNum: page.current,
      };
      if (nextFilters.status) payload.status = nextFilters.status;
      if (nextFilters.promotionStatus) {
        payload.promotionStatus = nextFilters.promotionStatus;
      }
      if (nextFilters.tier) payload.tier = nextFilters.tier;
      if (nextFilters.email?.trim()) payload.email = nextFilters.email.trim();

      return dispatch(listSubmissions(formId, payload))
        .then(() => {
          setTableParams({ pagination: { ...page } });
        })
        .finally(() => setLoading(false));
    },
    [dispatch, formId, filters, tableParams.pagination],
  );

  useEffect(() => {
    fetchSubmissions({ ...DEFAULT_PAGINATION, total: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
  }, [formId]);

  const applyFilters = () => {
    const nextFilters = { ...filters };
    if (emailInput.trim()) nextFilters.email = emailInput.trim();
    else delete nextFilters.email;
    setFilters(nextFilters);
    setSelectedRowKeys([]);
    fetchSubmissions({ ...tableParams.pagination, current: 1 }, nextFilters);
  };

  const handleTableChange = (pagination) => {
    fetchSubmissions({
      current: pagination.current,
      pageSize: pagination.pageSize,
      total: pagination.total,
    });
  };

  const handlePromote = () => {
    if (!selectedRowKeys.length) return;
    dispatch(promoteSubmissions(formId, selectedRowKeys))
      .then((result) => {
        const failed = result.results?.filter((r) => !r.ok) || [];
        if (failed.length) {
          message.warning(
            `Promoted with ${failed.length} error(s): ${failed.map((f) => f.error).join(", ")}`,
          );
        } else {
          message.success("Added to contacts");
        }
        setSelectedRowKeys([]);
        fetchSubmissions(tableParams.pagination);
      })
      .catch(() => message.error("Promotion failed"));
  };

  const handleReject = (status) => {
    if (!selectedRowKeys.length) return;
    dispatch(rejectSubmissions(formId, selectedRowKeys, status)).then(() => {
      message.success("Updated");
      setSelectedRowKeys([]);
      fetchSubmissions(tableParams.pagination);
    });
  };

  // PRD item 4 (FR-4.8), task 4.6. Restore didn't have a UI entry point at
  // all before this -- the backend route (routes/forms/index.js) has
  // existed since anti-spam shipped, but nothing in the app called it.
  const handleRestore = (record) => {
    dispatch(restoreSubmission(formId, record._id))
      .then(() => {
        message.success("Submission restored to inbox.");
        setDetail(null);
        fetchSubmissions(tableParams.pagination);
      })
      .catch(() => {});
  };

  const handleRetier = (record, tier) => {
    dispatch(retierSubmission(formId, record._id, tier))
      .then(() => {
        message.success(`Tier set to ${tier}.`);
        setDetail((prev) =>
          prev && prev._id === record._id
            ? { ...prev, leadTier: tier, leadTierOverridden: true }
            : prev,
        );
        fetchSubmissions(tableParams.pagination);
      })
      .catch(() => {});
  };

  const exportUrl = async (format) => {
    const token = localStorage.getItem("accessToken");
    try {
      const res = await axios.get(
        buildApiUrl(`/forms/${formId}/submissions/export`, { format }),
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: format === "csv" ? "blob" : "json",
        },
      );
      if (format === "csv") {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = `submissions-${formId}.csv`;
        a.click();
      } else {
        const blob = new Blob([JSON.stringify(res.data.result, null, 2)], {
          type: "application/json",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `submissions-${formId}.json`;
        a.click();
      }
      message.success("Export downloaded");
    } catch {
      message.error("Export failed");
    }
  };

  const customColumns = useMemo(() => {
    const fields = formRecord?.fields || [];
    return fields
      .filter((f) => f.showInTable && f.name && !IDENTITY_FIELD_NAMES.has(f.name))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((f) => ({
        title: f.label || f.name,
        key: `custom_${f.name}`,
        ellipsis: true,
        render: (_, r) => formatCustomFieldValue(r.payload?.[f.name]),
      }));
  }, [formRecord]);

  const columns = [
    {
      title: "Email",
      key: "email",
      ellipsis: true,
      render: (_, r) => r.normalized?.email || "—",
    },
    {
      title: "Name",
      key: "name",
      render: (_, r) =>
        [r.normalized?.firstName, r.normalized?.lastName]
          .filter(Boolean)
          .join(" ") || "—",
    },
    {
      title: "Phone",
      key: "phone",
      ellipsis: true,
      render: (_, r) => r.normalized?.phoneNumber || "—",
    },
    ...customColumns,
    {
      title: "Status",
      dataIndex: "status",
      render: (s, r) => (
        <Space direction="vertical" size={2}>
          <Tag color={statusColors[s]}>{s}</Tag>
          {(s === "spam" || s === "rejected") && (
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: "auto" }}
              onClick={() => handleRestore(r)}
            >
              Restore
            </Button>
          )}
        </Space>
      ),
    },
    {
      // PRD item 4 (FR-4.5/FR-4.7), task 4.6.
      title: "Lead tier",
      dataIndex: "leadTier",
      render: (tier, r) =>
        tier ? (
          <Tooltip
            title={
              r.leadSignals?.length
                ? r.leadSignals
                    .map(
                      (s) =>
                        `${humanizeSignalName(s.name)} (${s.weight >= 0 ? "+" : ""}${s.weight})`,
                    )
                    .join(", ")
                : "No signals"
            }
          >
            <Space size={4}>
              <Tag color={LEAD_TIER_COLORS[tier] || "default"}>
                {tier.toUpperCase()} · {r.leadScore ?? "—"}
              </Tag>
              {r.leadTierOverridden && <Tag>overridden</Tag>}
            </Space>
          </Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Promotion",
      dataIndex: "promotionStatus",
      render: (p, r) => (
        <Space direction="vertical" size={2}>
          <Space wrap>
            <Tag>{p}</Tag>
            {r.contactId && (
              <Button
                type="link"
                size="small"
                onClick={() => navigate("/app/contacts")}
              >
                View contacts
              </Button>
            )}
          </Space>
          {r.spamScore != null && (
            <Tooltip title={formatSpamReasons(r.spamReasons)}>
              <Tag color={spamScoreColor(r)} style={{ margin: 0 }}>
                Score {r.spamScore}
                {r.antiSpamWouldEnforce ? " · would block" : ""}
              </Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      render: (d) => (d ? new Date(d).toLocaleString() : "—"),
    },
    {
      title: "",
      key: "view",
      width: 88,
      fixed: "right",
      render: (_, r) => (
        <Button type="link" onClick={() => setDetail(r)}>
          Details
        </Button>
      ),
    },
  ];

  const showEmpty =
    submissionCount === 0 &&
    !loading &&
    !filters.status &&
    !filters.promotionStatus &&
    !filters.tier &&
    !filters.email;

  return (
    <>
      <Space wrap style={{ marginBottom: 16, width: "100%" }}>
        <Select
          placeholder="Status"
          allowClear
          className="app-filter-control--sm"
          value={filters.status}
          onChange={(v) =>
            setFilters((f) => {
              const next = { ...f };
              if (v == null || v === "") delete next.status;
              else next.status = v;
              return next;
            })
          }
          options={[
            { value: "received", label: "Received" },
            { value: "pending_confirm", label: "Pending confirm" },
            { value: "confirmed", label: "Confirmed" },
            { value: "spam", label: "Spam" },
            { value: "rejected", label: "Rejected" },
          ]}
        />
        <Select
          placeholder="Promotion"
          allowClear
          className="app-filter-control--sm"
          value={filters.promotionStatus}
          onChange={(v) =>
            setFilters((f) => {
              const next = { ...f };
              if (v == null || v === "") delete next.promotionStatus;
              else next.promotionStatus = v;
              return next;
            })
          }
          options={[
            { value: "inbox", label: "In inbox" },
            { value: "promoted", label: "Promoted" },
            { value: "skipped", label: "Skipped" },
          ]}
        />
        <Select
          placeholder="Lead tier"
          allowClear
          className="app-filter-control--sm"
          value={filters.tier}
          onChange={(v) =>
            setFilters((f) => {
              const next = { ...f };
              if (v == null || v === "") delete next.tier;
              else next.tier = v;
              return next;
            })
          }
          options={[
            { value: "hot", label: "Hot" },
            { value: "warm", label: "Warm" },
            { value: "cold", label: "Cold" },
          ]}
        />
        <Input
          placeholder="Search email"
          className="app-filter-control--md"
          value={emailInput}
          onChange={(e) => setEmailInput(e.target.value)}
          onPressEnter={applyFilters}
        />
        <Button type="primary" onClick={applyFilters}>
          Apply filters
        </Button>
        <Button
          type="primary"
          disabled={!selectedRowKeys.length}
          onClick={handlePromote}
        >
          Add to contacts ({selectedRowKeys.length})
        </Button>
        <Button
          disabled={!selectedRowKeys.length}
          onClick={() => handleReject("spam")}
        >
          Mark spam
        </Button>
        <Button onClick={() => exportUrl("csv")}>Export CSV</Button>
        <Button onClick={() => exportUrl("json")}>Export JSON</Button>
      </Space>

      {showEmpty ? (
        <Text type="secondary">No submissions yet.</Text>
      ) : (
        <div ref={tableAreaRef} style={{ width: "100%" }}>
          <Table
            rowKey="_id"
            loading={loading}
            columns={columns}
            dataSource={submissionList}
            scroll={{
              x: 1200,
              y: tableScrollY,
            }}
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys,
              getCheckboxProps: (record) => ({
                disabled:
                  record.promotionStatus === "promoted" ||
                  record.status === "spam" ||
                  record.status === "rejected",
              }),
            }}
            pagination={{
              ...tableParams.pagination,
              total: submissionCount,
              showQuickJumper: true,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (total) => `Total ${total} submissions`,
            }}
            onChange={handleTableChange}
          />
        </div>
      )}

      <Drawer
        title="Submission details"
        open={!!detail}
        onClose={() => setDetail(null)}
        width="min(480px, 90vw)"
      >
        {detail && (
          <Space direction="vertical" style={{ width: "100%" }}>
            {(detail.status === "spam" || detail.status === "rejected") &&
              detail.statusUpdatedBy && (
                <>
                  <Text strong>Status updated by</Text>
                  <Space>
                    <Text>
                      {detail.statusUpdatedBy}
                      {detail.statusUpdateSource
                        ? ` (${detail.statusUpdateSource})`
                        : ""}
                      {detail.statusUpdatedAt
                        ? ` · ${new Date(detail.statusUpdatedAt).toLocaleString()}`
                        : ""}
                    </Text>
                    <Button
                      type="link"
                      size="small"
                      onClick={() => handleRestore(detail)}
                    >
                      Restore
                    </Button>
                  </Space>
                </>
              )}
            {detail.leadTier && (
              <>
                <Text strong>Lead score</Text>
                <Space wrap align="center">
                  <Tag color={LEAD_TIER_COLORS[detail.leadTier] || "default"}>
                    {detail.leadTier.toUpperCase()} · {detail.leadScore}/100
                  </Tag>
                  {detail.leadTierOverridden && (
                    <Tag>Manually overridden</Tag>
                  )}
                  <Select
                    size="small"
                    style={{ width: 160 }}
                    value={detail.leadTier}
                    onChange={(tier) => handleRetier(detail, tier)}
                    options={[
                      { value: "hot", label: "Override: Hot" },
                      { value: "warm", label: "Override: Warm" },
                      { value: "cold", label: "Override: Cold" },
                    ]}
                  />
                </Space>
                {detail.leadSignals?.length > 0 && (
                  <>
                    <Text strong>Lead signals</Text>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {detail.leadSignals.map((s) => (
                        <li key={s.name}>
                          {humanizeSignalName(s.name)}{" "}
                          <Text type="secondary">
                            ({s.weight >= 0 ? "+" : ""}
                            {s.weight})
                          </Text>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                {detail.leadScoredAt && (
                  <Text type="secondary">
                    Scored {new Date(detail.leadScoredAt).toLocaleString()}
                  </Text>
                )}
              </>
            )}
            {detail.spamScore != null && (
              <>
                <Text strong>Anti-spam score</Text>
                <Space wrap>
                  <Tag color={spamScoreColor(detail)}>{detail.spamScore}</Tag>
                  {detail.antiSpamWouldEnforce && (
                    <Tag color="red">Would block at threshold</Tag>
                  )}
                  {detail.antiSpamMode && <Tag>{detail.antiSpamMode} mode</Tag>}
                </Space>
                {detail.spamReasons?.length > 0 && (
                  <>
                    <Text strong>Signals</Text>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {detail.spamReasons.map((r) => (
                        <li key={`${r.reason}-${r.weight}`}>
                          {r.reason} <Text type="secondary">(+{r.weight})</Text>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                {detail.antiSpamRescoredAt && (
                  <Text type="secondary">
                    Retroactively scored{" "}
                    {new Date(detail.antiSpamRescoredAt).toLocaleString()}
                  </Text>
                )}
              </>
            )}
            <Text strong>Normalized</Text>
            <pre>{JSON.stringify(detail.normalized, null, 2)}</pre>
            <Text strong>Raw payload</Text>
            <pre>{JSON.stringify(detail.payload, null, 2)}</pre>
            {detail.heuristicFlags && (
              <>
                <Text strong>Flags</Text>
                <pre>{JSON.stringify(detail.heuristicFlags, null, 2)}</pre>
              </>
            )}
          </Space>
        )}
      </Drawer>
    </>
  );
}
